import { createSelector } from 'reselect'

import {
  WEB_MARKETING_ADCASH_CTA_URL,
  WEB_MARKETING_LIVE_PREVIEW_DURATION,
  WEB_MARKETING_US_TRIAL_FLAG,
  WEB_MARKETING_CANADA_TRIAL_FLAG,
  WEB_MARKETING_LATINO_TRIAL_FLAG,
  WEB_MARKETING_CA_VALUE_PROPS,
  WEB_MARKETING_NHL_COUNTDOWN_TIMER,
  WEB_MARKETING_NBA_COUNTDOWN_TIMER,
  WEB_MARKETING_PPV_UEFA_WCQ,
  WEB_MARKETING_INTERNATIONAL_FRIENDLY_SCHEDULER,
  WEB_MARKETING_HERO_IMAGE,
  WEB_MARKETING_HEADER_SIGN_IN,
  WEB_MARKETING_PPV_INTEGRATED_SPORTS,
} from './constants'

import { StacFlags } from './flags'

import type { BooleanSelector, StacFlagName, StringSelector } from './types'
import { StacFlagsState } from './reducer'

export const selectStacFlagsState = (state): StacFlagsState => state.cohorts

export const selectStacFlagsReady: (state) => boolean = createSelector(
  selectStacFlagsState,
  ({ isInitialized, isFetching }) => isInitialized && !isFetching
)

export const selectAllStacFlags: (arg1) => StacFlags = createSelector(
  selectStacFlagsState,
  (local) => local?.flags
)

// allows passing of flagName and sets it as a cacheKey
const selectStacFlagName = <T>(_: T, flagName: StacFlagName): StacFlagName => flagName

// @ts-expect-error
export const selectStacFlagValue: <T>(arg1, arg2: StacFlagName) => T | null = createSelector(
  selectAllStacFlags,
  selectStacFlagName,
  (flags: StacFlags, flagName: StacFlagName) => {
    const flag = flags?.[flagName]
    return flag ? flag.value : null
  }
)

export const selectUsTrialFlag: BooleanSelector = createSelector(
  (state) => selectStacFlagValue<boolean>(state, WEB_MARKETING_US_TRIAL_FLAG),
  (value): boolean | null => value as boolean | null
)

export const selectLatinoTrialFlag: BooleanSelector = createSelector(
  (state) => selectStacFlagValue<boolean>(state, WEB_MARKETING_LATINO_TRIAL_FLAG),
  (value): boolean | null => value as boolean | null
)
export const selectCanadaTrialFlag: BooleanSelector = createSelector(
  (state) => selectStacFlagValue<boolean>(state, WEB_MARKETING_CANADA_TRIAL_FLAG),
  (value): boolean | null => value as boolean | null
)

export const selectAdcashCtaUrlFlag: BooleanSelector = createSelector(
  (state) => selectStacFlagValue(state, WEB_MARKETING_ADCASH_CTA_URL),
  (value) => value === 'test'
)

export const selectLivePreviewDurationFlag: StringSelector = createSelector(
  (state) => selectStacFlagValue<string>(state, WEB_MARKETING_LIVE_PREVIEW_DURATION),
  (value): string | null => value as string | null
)

export const selectCaValuePropsFlag: BooleanSelector = createSelector(
  (state) => selectStacFlagValue<string>(state, WEB_MARKETING_CA_VALUE_PROPS),
  (value) => value === 'test'
)

export const selectNhlCountdownTimerFlag: StringSelector = createSelector(
  (state) => selectStacFlagValue<string>(state, WEB_MARKETING_NHL_COUNTDOWN_TIMER),
  (value): string | null => value as string | null
)

export const selectNbaCountdownTimerFlag: StringSelector = createSelector(
  (state) => selectStacFlagValue<string>(state, WEB_MARKETING_NBA_COUNTDOWN_TIMER),
  (value): string | null => value as string | null
)

export const selectPpvUefaWcqFlag: StringSelector = createSelector(
  (state) => selectStacFlagValue<string>(state, WEB_MARKETING_PPV_UEFA_WCQ),
  (value): string | null => value as string | null
)

export const selectInternationalFriendlySchedulerFlag: StringSelector = createSelector(
  (state) => selectStacFlagValue<string>(state, WEB_MARKETING_INTERNATIONAL_FRIENDLY_SCHEDULER),
  (value): string | null => value as string | null
)

export const selectHeroImageFlag: StringSelector = createSelector(
  (state) => selectStacFlagValue<string>(state, WEB_MARKETING_HERO_IMAGE),
  (value): string | null => value as string | null
)

export const selectHeaderSignInFlag: BooleanSelector = createSelector(
  (state) => selectStacFlagValue<string>(state, WEB_MARKETING_HEADER_SIGN_IN),
  (value) => value === 'show_header'
)

export const selectPpvIntegratedSportsFlag: BooleanSelector = createSelector(
  (state) => selectStacFlagValue<string>(state, WEB_MARKETING_PPV_INTEGRATED_SPORTS),
  (value): boolean | null => value as boolean | null
)

// Default selector that returns null for unknown flags
const selectDefaultFlagValue: BooleanSelector & StringSelector = (_state) => null

export const selectCohortingFlagValue = (flagName: StacFlagName | null | undefined) => {
  if (!flagName) return selectDefaultFlagValue
  switch (flagName) {
    case WEB_MARKETING_ADCASH_CTA_URL:
      return selectAdcashCtaUrlFlag
    case WEB_MARKETING_LIVE_PREVIEW_DURATION:
      return selectLivePreviewDurationFlag
    case WEB_MARKETING_CA_VALUE_PROPS:
      return selectCaValuePropsFlag
    case WEB_MARKETING_NHL_COUNTDOWN_TIMER:
      return selectNhlCountdownTimerFlag
    case WEB_MARKETING_NBA_COUNTDOWN_TIMER:
      return selectNbaCountdownTimerFlag
    case WEB_MARKETING_PPV_UEFA_WCQ:
      return selectPpvUefaWcqFlag
    case WEB_MARKETING_INTERNATIONAL_FRIENDLY_SCHEDULER:
      return selectInternationalFriendlySchedulerFlag
    case WEB_MARKETING_HERO_IMAGE:
      return selectHeroImageFlag
    case WEB_MARKETING_HEADER_SIGN_IN:
      return selectHeaderSignInFlag
    case WEB_MARKETING_PPV_INTEGRATED_SPORTS:
      return selectPpvIntegratedSportsFlag
    default:
      return selectDefaultFlagValue
  }
}
